import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { NAVIGATION } from "@/app/navigation";
import { useSession } from "@/lib/auth/session";
import { cn } from "@/lib/utils/cn";

type SearchEntry = {
  label: string;
  group: string;
  to: string;
  requiredPermission: string;
  exact?: boolean;
};

const ENTRIES: SearchEntry[] = NAVIGATION.flatMap((g) => {
  const base: SearchEntry[] = [
    { label: g.label, group: g.label, to: g.to, requiredPermission: g.requiredPermission as string, exact: g.exactPermission },
  ];
  const children = (g.children ?? []).map((c) => ({
    label: c.label,
    group: g.label,
    to: c.to,
    requiredPermission: c.requiredPermission as string,
  }));
  return [...base, ...children];
});

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const hasPermission = useSession((s) => s.hasPermission);
  const explicit = useSession((s) => s.user?.permissions ?? []);

  useEffect(() => {
    if (open) {
      setQ("");
      setHighlight(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const results = useMemo(() => {
    const visible = ENTRIES.filter((e) => (e.exact ? explicit.includes(e.requiredPermission) : hasPermission(e.requiredPermission)));
    if (!q.trim()) return visible.slice(0, 12);
    const needle = q.toLowerCase();
    return visible
      .filter((e) =>
        (e.label + " " + e.group).toLowerCase().includes(needle),
      )
      .slice(0, 12);
  }, [q, hasPermission, explicit]);

  function jumpTo(entry: SearchEntry) {
    navigate(entry.to);
    onClose();
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4 bg-black/60 animate-fade-in"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-bg-elevated border border-border rounded-xl shadow-nx-xl overflow-hidden"
      >
        <div className="flex items-center gap-2 px-3 border-b border-border">
          <Search size={16} className="text-fg-subtle" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlight((h) => Math.min(h + 1, results.length - 1));
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlight((h) => Math.max(h - 1, 0));
              }
              if (e.key === "Enter" && results[highlight]) {
                e.preventDefault();
                jumpTo(results[highlight]);
              }
              if (e.key === "Escape") onClose();
            }}
            placeholder="Saltar a…"
            className="flex-1 bg-transparent py-3 text-base text-fg placeholder:text-fg-subtle outline-none"
          />
          <button
            onClick={onClose}
            className="p-1.5 rounded text-fg-subtle hover:bg-bg-hover hover:text-fg"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>

        <ul className="max-h-[50vh] overflow-y-auto py-1">
          {results.length === 0 && (
            <li className="px-4 py-8 text-center text-sm text-fg-subtle">
              Sin resultados.
            </li>
          )}
          {results.map((r, i) => (
            <li key={r.to}>
              <button
                onClick={() => jumpTo(r)}
                onMouseEnter={() => setHighlight(i)}
                className={cn(
                  "w-full text-left px-4 py-2 flex items-center justify-between gap-3 transition-colors",
                  i === highlight ? "bg-accent-soft text-fg" : "text-fg hover:bg-bg-hover",
                )}
              >
                <span className="text-sm font-medium">{r.label}</span>
                <span className="text-xs text-fg-subtle">{r.group}</span>
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between px-3 py-2 text-2xs text-fg-subtle bg-bg-card border-t border-border">
          <span>↑↓ navegar · ↵ saltar · Esc cerrar</span>
          <span>{results.length} resultado{results.length === 1 ? "" : "s"}</span>
        </div>
      </div>
    </div>
  );
}
